import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { Plus, Trash2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export default function KeywordsPanel() {
  const { authAxios } = useEditorAuth();
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ keyword: "", priority: "medium", notes: "" });
  const [filter, setFilter] = useState("all"); // all | unused | queued | used
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    authAxios().get("/editor/keywords").then((r) => setKeywords(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!form.keyword.trim()) { toast.error("Keyword required"); return; }
    try {
      await authAxios().post("/editor/keywords", form);
      toast.success("Keyword added");
      setForm({ keyword: "", priority: "medium", notes: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this keyword?")) return;
    try {
      await authAxios().delete(`/editor/keywords/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const updateStatus = async (kw, newStatus) => {
    try {
      await authAxios().put(`/editor/keywords/${kw.id}`, { ...kw, status: newStatus });
      load();
    } catch { toast.error("Update failed"); }
  };

  const generateFrom = (kw) => {
    // Navigate to new article editor with keyword pre-populated as query param
    navigate(`/editor/articles/new?keyword=${encodeURIComponent(kw.keyword)}`);
  };

  const filtered = keywords.filter(k => filter === "all" || k.status === filter);
  const sorted = [...filtered].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2));

  const stats = {
    total: keywords.length,
    unused: keywords.filter(k => k.status === "unused").length,
    queued: keywords.filter(k => k.status === "queued").length,
    used: keywords.filter(k => k.status === "used").length,
  };

  return (
    <div data-testid="keywords-panel">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-sm text-gray-500">{stats.total} total · {stats.unused} unused · {stats.queued} queued · {stats.used} used</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex text-xs border border-gray-300">
            {["all", "unused", "queued", "used"].map((f) => (
              <button
                key={f} onClick={() => setFilter(f)} data-testid={`kw-filter-${f}`}
                className={`px-3 py-1.5 uppercase tracking-wider ${filter === f ? "bg-[#0A1128] text-white" : "text-gray-600 hover:bg-gray-100"}`}
              >{f}</button>
            ))}
          </div>
          <button onClick={() => setShowForm(true)} data-testid="kw-add-open" className="btn-primary text-sm">
            <Plus className="w-4 h-4"/> Add Keyword
          </button>
        </div>
      </div>

      {showForm && (
        <div className="border border-gray-200 bg-white p-6 mb-6" data-testid="kw-form">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">New Keyword</div>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-[#0A1128]"><X className="w-4 h-4"/></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="sm:col-span-2">
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Keyword *</label>
              <input data-testid="kw-input" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} placeholder="e.g. Filipino helper salary Singapore 2026" className="w-full border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Priority</label>
              <select data-testid="kw-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E]">
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Notes (optional)</label>
              <input data-testid="kw-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowForm(false)} className="text-sm text-gray-500 hover:text-[#0A1128] px-4 py-2">Cancel</button>
            <button onClick={add} data-testid="kw-submit" className="btn-primary text-sm">Add</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">No keywords in this filter.</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="kw-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F7]">
              <tr className="text-left text-xs tracking-widest uppercase text-gray-500">
                <th className="px-6 py-4">Keyword</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Notes</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((k) => (
                <tr key={k.id} className="border-t border-gray-100 hover:bg-[#F9F9F7]" data-testid={`kw-row-${k.id}`}>
                  <td className="px-6 py-4 font-medium text-[#0A1128]">{k.keyword}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 border ${
                      k.priority === "high" ? "border-red-400 text-red-600" :
                      k.priority === "medium" ? "border-amber-400 text-amber-700" :
                      "border-gray-300 text-gray-500"
                    }`}>{k.priority}</span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={k.status}
                      onChange={(e) => updateStatus(k, e.target.value)}
                      data-testid={`kw-status-${k.id}`}
                      className={`text-xs px-2 py-1 border bg-transparent focus:outline-none ${
                        k.status === "used" ? "border-[#688A4E] text-[#688A4E]" :
                        k.status === "queued" ? "border-amber-500 text-amber-600" :
                        "border-gray-300 text-gray-500"
                      }`}
                    >
                      <option value="unused">Unused</option>
                      <option value="queued">Queued</option>
                      <option value="used">Used</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-600 max-w-xs truncate">{k.notes || "—"}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-3">
                      <button onClick={() => generateFrom(k)} data-testid={`kw-generate-${k.id}`} className="text-xs text-[#688A4E] hover:text-[#56763E] flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5"/> Write
                      </button>
                      <button onClick={() => del(k.id)} data-testid={`kw-delete-${k.id}`} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
